import { useState } from 'react'
import { Box } from '@mui/material'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import "@fontsource/rubik";
import CustomButton from '../../../components/CustomButton'
import usePermissions from '../../../hooks/usePermissions'
import { PERMISSION } from '../../../constants/constants'
import { downloadS3File } from '../../../utils/commonServices'

/**
 * Download / share controls for one delivered version. Shared by the in-card
 * history panel and the standalone history popup so both obey the same rules:
 *
 *  - Download appears only when `download_url` is non-null (today the accepted
 *    version only). A download link is never built from `video_url`.
 *  - The share link appears only on a version that is both `latest` and
 *    `accepted`. `/watchvideo/<slug>` opens that exact cut and is public, so a
 *    link on a superseded version would hand out a cut that has since been
 *    replaced, and a link before acceptance would hand out one still under
 *    review. On every other version `slug` is informational only.
 *
 * `compact` narrows the buttons for the side panel, which is far narrower than
 * the popup.
 */
const RevisionVersionActions = (props) => {
  const { version, compact } = props;
  const [hasPermission] = usePermissions()
  const [copyButtonText, setCopyButtonText] = useState('Copy link');

  if (!version) return null;

  const canDownload = hasPermission(PERMISSION.PROJECT.REVISION.FINAL_VIDEO.DOWNLOAD)
  const showCopyLink = version?.latest === true && version?.accepted === true && !!version?.slug;

  const downloadHandler = () => {
    if (version?.download_url) downloadS3File(version.download_url)
  }

  const copyLinkHandler = () => {
    setCopyButtonText('Link Copied!')
    navigator.clipboard.writeText(`${window.location.origin}/watchvideo/${version?.slug}`)
    setTimeout(() => {
      setCopyButtonText('Copy link')
    }, 1500)
  }

  if (!showCopyLink && !version?.download_url) return null;

  const buttonSx = compact
    ? {
        fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '12px' },
        fontWeight: 600,
        height: { xs: '16px', sm: '18px', md: '20px', lg: '22px', xl: '30px' },
        width: { xs: '72px', md: '84px', lg: '92px', xl: '124px' },
        fontFamily: 'Rubik',
      }
    : {
        fontSize: { xs: '8px', sm: '9px', md: '10px', lg: '11px', xl: '16px' },
        fontWeight: 600,
        height: { xs: '18px', sm: '20px', md: '22px', lg: '24px', xl: '32px' },
        width: { xs: '90px', md: '110px', lg: '125px', xl: '165px' },
        fontFamily: 'Rubik',
      };

  return (
    <Box className='flex a-center' gap={1} sx={{ flexWrap: 'wrap' }}>
      {showCopyLink && (
        <CustomButton
          btnText={copyButtonText}
          data-testid='revision-history-copy-link'
          disabled={!canDownload}
          onClick={copyLinkHandler}
          component='button'
          type='button'
          color='secondary'
          variant='contained'
          endIcon={<ContentCopyIcon sx={{ fontSize: compact ? '10px !important' : '12px !important' }} />}
          style={{ borderRadius: '5px', border: 'none', textTransform: 'none' }}
          sx={buttonSx}
        />
      )}

      {!!version?.download_url && (
        <CustomButton
          btnText='Download'
          data-testid='revision-history-download'
          disabled={!canDownload}
          onClick={downloadHandler}
          component='button'
          type='button'
          color='secondary'
          variant='contained'
          endIcon={<DownloadRoundedIcon sx={{ fontSize: compact ? '12px !important' : '14px !important' }} />}
          style={{ borderRadius: '5px', border: 'none', textTransform: 'none' }}
          sx={buttonSx}
        />
      )}
    </Box>
  )
}

export default RevisionVersionActions
